import * as React from 'react';
import * as ReactDom from 'react-dom';
import { useIdleTimer } from "react-idle-timer";
import { AuthProvider, useAuth, AuthProviderProps, hasAuthParams } from 'react-oidc-context';
import "bootstrap-sass/assets/javascripts/bootstrap.js";
import configureStore from './store';
import { initialState } from './reducers';
import CoreApp from './CoreApp';
import { Provider } from 'react-redux';
import DocumentTitle from '../misc/components/DocumentTitle';
import { InitFontLibrary } from './FontAwesomeLibrary';
import { CookiesProvider } from 'react-cookie';
import { BrowserRouter, useHistory } from 'react-router-dom';
import LoadingWithMessage from '../misc/components/LoadingWithMessage';
import { SessionExpiringModal } from '../Account/SessionExpiringModal';
import { User, WebStorageStateStore } from 'oidc-client-ts';
import { Unauthorized } from '../errors/unauthorized/Unauthorized';
import { IdleTimerContextProvider } from './IdleTimerContext';
import { AboutContextProvider } from './AboutContextProvider';
import { WebPortalLegacyClientInternal, useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';
import { SignInError } from '../errors/SignInError';

const store = configureStore(initialState);

InitFontLibrary();

declare const rootUrl: string;


const ApplicationRoot = () => {
    return (
        <DocumentTitle title="Vitalograph Web Portal">
            <BrowserRouter>
                <Root baseRoot={rootUrl} />
            </BrowserRouter>
        </DocumentTitle>
    )
}

export const SaveCurrentPath = (history) => {
    const currentPath = history.location.pathname;
    if (currentPath.includes("/account/study"))
        return;

    window.sessionStorage.setItem("WebPortal.PreviousPath", currentPath);
}

interface AuthorizedProps {
    baseRoot: string;
    logoutRedirectUri: string;
    children: any;
}

const Authorized = ({ baseRoot, logoutRedirectUri, children }: AuthorizedProps) => {
    const { user, isLoading, activeNavigator, error, events, isAuthenticated, signinRedirect, signoutRedirect } = useAuth();
    const history = useHistory();
    const [sessionExpiryVisible, setSessionExpiryVisible] = React.useState(false);

    const onIdle = () => {
        setSessionExpiryVisible(true);
    }

    const timer = useIdleTimer({
        name: "WebPortalIdleTimer",
        onIdle,
        timeout: 1000 * 60 * 15,
    });

    React.useEffect(() => {
        return events.addAccessTokenExpired(() => {
            signoutRedirect({ post_logout_redirect_uri: `${baseRoot}${logoutRedirectUri}` });
        });
    }, [events, signoutRedirect]);

    React.useEffect(() => {
        SaveCurrentPath(history);
        if (!hasAuthParams() && !isAuthenticated && !activeNavigator && !isLoading) {
            signinRedirect({ redirectMethod: "replace" });
        }
        
    }, [isAuthenticated, activeNavigator, isLoading, signinRedirect]);

    switch (activeNavigator) {
        case "signinRedirect":
            return <LoadingWithMessage message="Redirecting to sign in..." />;
        case "signoutRedirect":
            return <LoadingWithMessage message="Signing you out..." />;
    }

    if (error) {
        return <SignInError />
    }


    if (!user && isLoading)
        return <LoadingWithMessage message="Loading..." />;

    if (!isAuthenticated)
        return <Unauthorized />;

    return (<>
        {sessionExpiryVisible ? <SessionExpiringModal logoutRedirectUri={logoutRedirectUri} onClose={() => setSessionExpiryVisible(false)} /> : null}
        <IdleTimerContextProvider timer={timer}>
            {children}
        </IdleTimerContextProvider>
    </>);
}

interface RootProps {
    baseRoot: string;
}

const Root = ({ baseRoot }: RootProps) => {
    const { replace, push } = useHistory();
    const [about, setAbout] = React.useState<any>();


    React.useEffect(() => {
        const init = async () => {
            const result = await fetch(rootUrl + "about")
                .then(response => response.json());

            setAbout(result);
        }

        init();
    }, []);

    const handleSignIn = async (user: User) => {
        await handlePostLogin(user);

        if (window.location.pathname === "/underapproval" || window.location.pathname === "/unauthorized")
            return;

        const prevPath = window.sessionStorage.getItem("WebPortal.PreviousPath");
        if (prevPath === "/account/signedout") {
            replace('/account/study');
            return;
        }

        window.location.replace(prevPath ?? '/account/study');
    }


    const handlePostLogin = async (user: User) => {
        if (!user) return;

        const legacyClient = new WebPortalLegacyClientInternal(rootUrl, {
            fetch: (url, init) => {
                const authInit = user?.access_token
                    ? {
                        ...init, credentials: 'same-origin',
                        headers: { ...init?.headers, 'Authorization': `Bearer ${user?.access_token}` }
                    } as RequestInit
                    : init;

                return fetch(url, authInit);
            }
        });

        await legacyClient.postLogin().catch(error => {
            console.log(error)
            if (error === "User requires approval")
                push("/underapproval");
            else
                push("/unauthorized");
            return;
        });
        await legacyClient.LogSignInAuditTrail(false);

    }

    if (!about)
        return <LoadingWithMessage message="Loading..." />;

    const oidcConfiguration: AuthProviderProps = {
        authority: about.Authority,
        client_id: about.ClientId,
        scope: about.Scopes,
        redirect_uri: `${baseRoot}${about.SignInRedirect}`,
        automaticSilentRenew: true,
        loadUserInfo: true,
        revokeTokensOnSignout: true,
        accessTokenExpiringNotificationTimeInSeconds: 60,
        onSigninCallback: handleSignIn,
        userStore: new WebStorageStateStore({ store: window.sessionStorage, prefix: "oidc.vitalographwebportal" }),
        stateStore: new WebStorageStateStore({ store: window.sessionStorage, prefix: "oidc.vitalographwebportal" }),
    };

    (window as any).accountServerUrl = about.Authority;
    (window as any).coughMarshalRootUrl = about.CoughMarshalUri;

    return (
        <AuthProvider {...oidcConfiguration}>
            <Authorized baseRoot={baseRoot} logoutRedirectUri={about.SignOutRedirect}>
                <AboutContextProvider aboutProp={about}>
                    <CoreApp version={about.Version} logoutRedirectUri={about.SignOutRedirect} />
                </AboutContextProvider>
            </Authorized>
        </AuthProvider>
    );
}

ReactDom.render(
    (
        <Provider store={store}>
            <CookiesProvider>
                <ApplicationRoot />
            </CookiesProvider>
        </Provider>),
    document.getElementById('coreRoot')
);


